import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

type LogDetailPopupProps = {
    data: any;
    setShowModal: (value: boolean) => void;
    showModal: boolean;
};

export const SMSLogDetailPopup = ({
    data,
    setShowModal,
    showModal
}: LogDetailPopupProps) => {
    const navigate = useNavigate();

    const closeModal = () => {
        setShowModal(false);
    }

    const formatDate = (value?: string | Date | null) => {
        if (!value) return "No date";

        try {
            return format(new Date(value), "MM/dd/yyyy hh:mm a");
        } catch {
            return "No date";
        }
    }

    return (
        <div className={`fixed inset-0 p-4 ${showModal ? "flex" : "hidden"} justify-center items-center w-full h-full z-[1000] bg-black bg-opacity-50`}>
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white shadow-lg rounded-md px-5 py-4 relative">
                <button
                    type="button"
                    onClick={closeModal}
                    className="absolute right-5 top-5 text-gray-500 hover:text-red-500"
                    aria-label="Close"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        viewBox="0 0 320.591 320.591"
                        fill="currentColor"
                    >
                        <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                        <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                    </svg>
                </button>

                <div className="my-2">
                    <div className="text-center mb-6">
                        <h4 className="text-xl text-secondary font-semibold">
                            SMS Log Detail
                        </h4>
                    </div>

                    <div className="grid grid-cols-1 xm:grid-cols-2 gap-4 my-4">
                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                Sender Phone Number
                            </label>

                            <span className="w-full text-sm text-black mt-2 inline-block text-left break-all">
                                {data?.senderPhoneNumber || "-"}
                            </span>
                        </div>

                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                Receiver Phone Number
                            </label>

                            <span className="w-full text-sm text-black mt-2 inline-block text-left break-all">
                                {data?.receiverPhoneNumber || "-"}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xm:grid-cols-2 gap-4 my-4">
                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                {data?.entityType === "Leadership" ? "Leadership Name" : "Member Name"}
                            </label>

                            {
                                data?.entityId ? (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            data.entityType === "Leadership"
                                                ? navigate(`/admin/leadership/editLeadership/${data.entityId}`)
                                                : navigate(`/admin/members/editMember/${data.entityId}`)
                                        }
                                        className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                    >
                                        {data?.entityName}
                                    </button>
                                ) : (
                                    <span className="w-full text-sm text-black mt-2 inline-block text-left">
                                        -
                                    </span>
                                )
                            }
                        </div>

                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                {data?.entityType === "Leadership" ? "Leadership Team Name" : "Ministry Name"}
                            </label>

                            <span className="w-full text-sm text-black mt-2 inline-block">
                                {data?.entityTypeName || (data?.entityType === "Leadership" ? "All Leadership" : "All Member")}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xm:grid-cols-2 gap-4 my-4">
                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                Status
                            </label>

                            <span
                                className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium ${data?.status === "Sent"
                                    ? "bg-green-100 text-green-700"
                                    : data?.status === "Failed"
                                        ? "bg-red-100 text-red-700"
                                        : data?.status === "Queued"
                                            ? "bg-yellow-100 text-yellow-700"
                                            : "bg-gray-100 text-gray-700"
                                    }`}
                            >
                                {data?.status || "-"}
                            </span>
                        </div>

                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                Cost
                            </label>

                            <span className="w-full text-sm text-black mt-2 inline-block text-left">
                                {data?.cost != null ? `$${Number(data.cost).toFixed(2)}` : "-"}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xm:grid-cols-2 gap-4 my-4">
                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                Sent By
                            </label>

                            <span className="w-full text-sm text-black mt-2 inline-block text-left break-all">
                                {data?.sentBy || "-"}
                            </span>
                        </div>

                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                Sent At
                            </label>

                            <span className="w-full text-sm text-black mt-2 inline-block text-left">
                                {formatDate(data?.sentAt)}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xm:grid-cols-2 gap-4 my-4">
                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                Created At
                            </label>

                            <span className="w-full text-sm text-black mt-2 inline-block text-left">
                                {formatDate(data?.createdAt)}
                            </span>
                        </div>

                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                Currency
                            </label>

                            <span className="w-full text-sm text-black mt-2 inline-block text-left">
                                {data?.currency || "-"}
                            </span>
                        </div>
                    </div>

                    <div className="my-5">
                        <label className="block text-secondary text-xs mb-2">
                            Message
                        </label>

                        <div className="w-full min-h-[100px] max-h-[200px] overflow-y-auto rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-black text-left whitespace-pre-wrap break-words">
                            {data?.message || "No message"}
                        </div>
                    </div>

                    {
                        data?.errorMessage && (
                            <div className="my-5">
                                <label className="block text-red-600 text-xs mb-2">
                                    Error Message
                                </label>

                                <div className="w-full rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700 text-left whitespace-pre-wrap break-words">
                                    {data.errorMessage}
                                </div>
                            </div>
                        )
                    }

                    <div className="flex max-xm:flex-col gap-4 mt-6">
                        <button
                            type="button"
                            className="px-6 py-2 min-w-[150px] w-full rounded text-black text-sm font-semibold border-none outline-none bg-gray-200 hover:bg-gray-300 active:bg-gray-200"
                            onClick={closeModal}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};